import { api } from './api'
import type {
  MemberPlotDetail,
  PlotDetail,
  PlotDevelopmentStatus,
  PlotImportPreviewResult,
  PlotImportResult,
  PlotImportRowInput,
  PlotListItem,
  PlotOwnershipHistoryItem,
} from '@riverpark/types'

export interface ListPlotsParams {
  page?: number
  limit?: number
  search?: string
  status?: 'occupied' | 'reserved' | 'available'
  section?: string
  plotType?: 'residential' | 'special_use'
}

export interface ListPlotsResponse {
  data: PlotListItem[]
  meta: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface CreatePlotInput {
  newNumber: string
  oldNumber?: string
  plotType?: 'residential' | 'special_use'
  developmentStatus?: PlotDevelopmentStatus
  section?: string
  mainAvenue?: string
  courtOrStreet?: string
  notes?: string
  boreholeMeterNumber?: string
  kplcMeterNumber?: string
  status?: 'occupied' | 'reserved' | 'available'
}

export interface UpdatePlotInput {
  developmentStatus?: PlotDevelopmentStatus
  section?: string
  mainAvenue?: string
  courtOrStreet?: string
  notes?: string
  boreholeMeterNumber?: string
  kplcMeterNumber?: string
  status?: 'occupied' | 'reserved' | 'available'
}

export async function listPlotsPaginated(params: ListPlotsParams = {}) {
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.search) qs.set('search', params.search)
  if (params.status) qs.set('status', params.status)
  if (params.section) qs.set('section', params.section)
  if (params.plotType) qs.set('plotType', params.plotType)
  const query = qs.toString()
  return api.get<ListPlotsResponse>(`/plots${query ? `?${query}` : ''}`)
}

export async function getPlot(id: string) {
  return api.get<{ data: PlotDetail }>(`/plots/${id}`)
}

export async function getMyPlot(plotId: string) {
  return api.get<{ data: MemberPlotDetail }>(`/plots/my/${plotId}`)
}

export async function getPlotHistory(id: string) {
  return api.get<{ data: PlotOwnershipHistoryItem[] }>(`/plots/${id}/history`)
}

export async function createPlot(body: CreatePlotInput) {
  return api.post<{ data: PlotDetail }>('/plots', body)
}

export async function updatePlot(id: string, body: UpdatePlotInput) {
  return api.patch<{ data: PlotDetail }>(`/plots/${id}`, body)
}

export async function assignPlotOwner(
  plotId: string,
  memberId: string,
  transferNote?: string,
  overrideDuesReason?: string,
) {
  return api.post<{ data: PlotDetail }>(`/plots/${plotId}/assign`, {
    memberId,
    transferNote,
    overrideDuesReason,
  })
}

export async function releasePlotOwner(
  plotId: string,
  transferNote?: string,
  overrideDuesReason?: string,
) {
  return api.post<{ data: PlotDetail }>(`/plots/${plotId}/release`, {
    transferNote,
    overrideDuesReason,
  })
}

export async function previewPlotImport(rows: PlotImportRowInput[]) {
  return api.post<{ data: PlotImportPreviewResult }>('/plots/import/preview', { rows })
}

export async function confirmPlotImport(rows: PlotImportRowInput[]) {
  return api.post<{ data: PlotImportResult }>('/plots/import', { rows })
}
